import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Check, CreditCard, Flame, Search, Users, Utensils } from 'lucide-react';
import { getRef } from '../lib/referral';
import { useT } from '../i18n';

const API = process.env.REACT_APP_BACKEND_URL;

export const FLAGSHIPS = {
  indexai: {
    icon: Search, name: 'Index AI™', price: 99, href: '/index-ai',
    tag: { en: 'AI Search Visibility Platform', es: 'Plataforma de Visibilidad en Búsqueda IA' },
    pitch: { en: 'Be the restaurant ChatGPT, Gemini and Google AI recommend.', es: 'Sé el restaurante que ChatGPT, Gemini y Google AI recomiendan.' },
    cta: { en: 'Start with Index AI', es: 'Empieza con Index AI' },
  },
  workforce: {
    icon: Users, name: 'AI Workforce™', price: 199, href: '/ai-agents-for-restaurants',
    tag: { en: 'Restaurant AI Employees', es: 'Empleados IA para tu Restaurante' },
    pitch: { en: 'Rachel and her team answer calls, reviews and guests 24/7.', es: 'Rachel y su equipo atienden llamadas, reseñas y clientes 24/7.' },
    cta: { en: 'Build My AI Team', es: 'Crea mi Equipo IA' },
  },
  catering: {
    icon: Utensils, name: 'Catering AI™', price: 399, href: '/ai/catering-ai',
    tag: { en: 'Corporate Catering Platform', es: 'Plataforma de Catering Corporativo' },
    pitch: { en: 'Turn corporate catering into a predictable revenue pipeline.', es: 'Convierte el catering corporativo en un pipeline predecible.' },
    cta: { en: 'Launch Catering AI', es: 'Lanza Catering AI' },
  },
};

/* Funnel closer: price + founding urgency + direct CTAs. Drop into any product page. */
export const ProductPricingCTA = ({ product }) => {
  const { lang } = useT();
  const isEs = lang === 'es';
  const p = FLAGSHIPS[product];
  const Icon = p.icon;
  const [payLink, setPayLink] = useState(null);
  const [nativeCheckout, setNativeCheckout] = useState(false);
  const [paypalOn, setPaypalOn] = useState(false);
  const [paying, setPaying] = useState(false);
  const [paypalPaying, setPaypalPaying] = useState(false);
  useEffect(() => {
    fetch(`${API}/api/payments/links`).then((r) => r.json()).then((d) => {
      setPayLink(d[product] || null);
      setNativeCheckout(!!d.native_checkout);
      setPaypalOn(!!d.paypal);
    }).catch(() => {});
  }, [product]);
  const stripeHref = payLink
    ? `${payLink}${payLink.includes('?') ? '&' : '?'}client_reference_id=${encodeURIComponent(getRef() || 'DIRECT')}`
    : null;
  const startCheckout = async () => {
    setPaying(true);
    try {
      const r = await fetch(`${API}/api/payments/checkout/session`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ product, origin_url: window.location.origin, ref_code: getRef() || null }),
      });
      const d = await r.json();
      if (d.url) { window.location.href = d.url; return; }
      throw new Error('no url');
    } catch {
      setPaying(false);
    }
  };
  const startPaypal = async () => {
    setPaypalPaying(true);
    try {
      const r = await fetch(`${API}/api/payments/paypal/subscription`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ product, origin_url: window.location.origin, ref_code: getRef() || null }),
      });
      const d = await r.json();
      if (d.url) { window.location.href = d.url; return; }
      throw new Error('no url');
    } catch {
      setPaypalPaying(false);
    }
  };
  return (
    <section className="py-16 lg:py-20 bg-gradient-to-b from-orange-50/50 to-white" data-testid={`product-pricing-${product}`}>
      <div className="max-w-[880px] mx-auto px-6">
        <div className="rounded-3xl border border-[#F77F11]/25 bg-white shadow-[0_40px_100px_-45px_rgba(247,127,17,0.45)] p-7 sm:p-10 text-center">
          <span className="inline-flex w-12 h-12 rounded-2xl bg-neutral-950 text-white items-center justify-center">
            <Icon className="w-5.5 h-5.5 w-5 h-5" />
          </span>
          <h2 className="mt-4 text-2xl sm:text-3xl font-bold text-neutral-900 tracking-[-0.01em]">{p.name}</h2>
          <p className="mt-1 text-[12px] tracking-[0.18em] uppercase font-semibold text-neutral-400">{isEs ? p.tag.es : p.tag.en}</p>
          <p className="mt-5 text-[12.5px] text-neutral-500">{isEs ? 'Desde' : 'Starting from'}</p>
          <p className="text-5xl font-bold text-neutral-900">${p.price}<span className="text-lg font-medium text-neutral-500">/{isEs ? 'mes' : 'month'}</span></p>
          <p className="mt-2 inline-flex items-center gap-1.5 text-[12.5px] font-semibold text-[#F77F11]">
            <Flame className="w-3.5 h-3.5" />
            {isEs ? 'Precio de lanzamiento — tu tarifa se congela al contratar' : 'Founding price — your rate locks in when you join'}
          </p>
          <div className="mt-7 flex flex-col sm:flex-row justify-center gap-3">
            {stripeHref ? (
              <a href={stripeHref} target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center gap-2 bg-[#F77F11] hover:bg-[#d8651a] text-white h-12 px-8 rounded-full text-[15px] font-bold transition-colors" data-testid={`product-buy-${product}`}>
                <CreditCard className="w-4 h-4" /> {isEs ? `Contratar ahora — $${p.price}/mes` : `Buy now — $${p.price}/mo`}
              </a>
            ) : nativeCheckout ? (
              <button onClick={startCheckout} disabled={paying} className="inline-flex items-center justify-center gap-2 bg-[#F77F11] hover:bg-[#d8651a] disabled:opacity-60 text-white h-12 px-8 rounded-full text-[15px] font-bold transition-colors" data-testid={`product-buy-${product}`}>
                <CreditCard className="w-4 h-4" /> {paying ? (isEs ? 'Abriendo pago seguro…' : 'Opening secure checkout…') : (isEs ? `Contratar ahora — $${p.price}/mes` : `Buy now — $${p.price}/mo`)}
              </button>
            ) : (
              <Link to={`/contact?platform=${product}`} className="inline-flex items-center justify-center gap-2 bg-[#F77F11] hover:bg-[#d8651a] text-white h-12 px-8 rounded-full text-[15px] font-bold transition-colors" data-testid={`product-cta-${product}`}>
                {isEs ? p.cta.es : p.cta.en} <ArrowRight className="w-4 h-4" />
              </Link>
            )}
            {paypalOn && (
              <button onClick={startPaypal} disabled={paypalPaying} className="inline-flex items-center justify-center gap-2 bg-[#FFC439] hover:bg-[#f2b636] disabled:opacity-60 text-[#111827] h-12 px-7 rounded-full text-[15px] font-bold transition-colors" data-testid={`product-paypal-${product}`}>
                {paypalPaying
                  ? (isEs ? 'Abriendo PayPal…' : 'Opening PayPal…')
                  : <><span className="italic font-extrabold tracking-tight"><span className="text-[#003087]">Pay</span><span className="text-[#0070E0]">Pal</span></span> — ${p.price}/{isEs ? 'mes' : 'mo'}</>}
              </button>
            )}
            <Link to={`/contact?platform=${product}&type=discovery`} className="inline-flex items-center justify-center gap-2 border border-neutral-300 text-neutral-700 hover:border-[#F77F11] hover:text-[#F77F11] h-12 px-7 rounded-full text-[14px] font-semibold transition-colors" data-testid={`product-discovery-${product}`}>
              {isEs ? 'Agendar Discovery Call gratis' : 'Book a Free Discovery Call'}
            </Link>
          </div>
          <div className="mt-6 flex flex-wrap justify-center gap-x-6 gap-y-2 text-[12.5px] text-neutral-500">
            {(isEs
              ? ['Sin permanencia', 'Cancela cuando quieras', 'Resultados desde el primer mes']
              : ['No lock-in', 'Cancel anytime', 'Results from month one']
            ).map((x) => (
              <span key={x} className="inline-flex items-center gap-1.5"><Check className="w-3.5 h-3.5 text-emerald-500" /> {x}</span>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

/* Compact cross-sell strip: the 3 flagship products, highlighting the ones not on the current page. */
export const FlagshipStrip = ({ current }) => {
  const { lang } = useT();
  const isEs = lang === 'es';
  return (
    <section className="py-14 bg-neutral-50/70 border-t border-neutral-100" data-testid="flagship-strip">
      <div className="max-w-[1180px] mx-auto px-6 lg:px-10">
        <p className="text-center text-[11px] tracking-[0.22em] uppercase font-bold text-[#F77F11]">
          {isEs ? 'LAS TRES PLATAFORMAS ESTRELLA DE KITXENS' : 'THE THREE KITXENS FLAGSHIP PLATFORMS'}
        </p>
        <div className="mt-6 grid sm:grid-cols-3 gap-4">
          {Object.entries(FLAGSHIPS).map(([id, p]) => {
            const Icon = p.icon;
            const isCurrent = id === current;
            return (
              <Link
                key={id}
                to={isCurrent ? '/pricing' : p.href}
                className={`group rounded-2xl border p-5 transition-all ${isCurrent ? 'border-[#F77F11]/50 bg-orange-50/50' : 'border-neutral-200 bg-white hover:border-[#F77F11]/60 hover:-translate-y-0.5 hover:shadow-lg'}`}
                data-testid={`flagship-card-${id}`}
              >
                <div className="flex items-center justify-between">
                  <span className="w-9 h-9 rounded-xl bg-neutral-950 text-white flex items-center justify-center"><Icon className="w-4 h-4" /></span>
                  {isCurrent && (
                    <span className="text-[10px] font-bold tracking-wide uppercase text-[#F77F11] bg-white border border-[#F77F11]/30 rounded-full px-2.5 py-1">
                      {isEs ? 'Estás aquí' : "You're here"}
                    </span>
                  )}
                </div>
                <p className="mt-3.5 font-bold text-neutral-900 text-[15.5px]">{p.name}</p>
                <p className="mt-1 text-[13px] text-neutral-500 leading-relaxed">{isEs ? p.pitch.es : p.pitch.en}</p>
                <p className="mt-3 text-[13px] font-semibold text-neutral-800">
                  {isEs ? `Desde $${p.price}/mes` : `From $${p.price}/mo`}
                  <ArrowRight className="inline w-3.5 h-3.5 ml-1.5 text-[#F77F11] group-hover:translate-x-0.5 transition-transform" />
                </p>
              </Link>
            );
          })}
        </div>
      </div>
    </section>
  );
};
